import { Dispatch } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { MiniTableRow } from '@/components/tables/MiniTable/MiniTable';
import { TrustDetailsTabs } from '@/modules/entities/details/entityDetails.types';
import { SCHEDULED_DISTRIBUTION_FREQUENCY_ITEMS } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { useEstateWaterfallContext } from '@/modules/estateWaterfall/contexts/estateWaterfall.context';
import { EstateWaterfall_TransferLogItemFragment } from '@/modules/estateWaterfall/graphql/EstateWaterfall.generated';
import { EstateWaterfallAction } from '@/modules/estateWaterfall/types';
import { createEdgeId } from '@/modules/estateWaterfall/waterfallGraph/waterfallGraph';
import { ScheduledDistributionKind } from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatPercentSmart } from '@/utils/formatting/percent';

import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface ScheduledDistributionCardParams {
  source: string;
  target: string;
  sourceEntityName: string;
  targetEntityName: string;
  payments: EstateWaterfall_TransferLogItemFragment[];
  dispatch: Dispatch<EstateWaterfallAction>;
}

function ViewBeneficiariesButton({
  source,
  edgeId,
  dispatch,
}: Pick<ScheduledDistributionCardParams, 'source' | 'dispatch'> & {
  edgeId: string;
}) {
  const { state } = useEstateWaterfallContext();
  if (state.presentationMode) return null;

  return (
    <Button
      variant="primary"
      size="xs"
      sx={{ width: 'fit-content' }}
      onClick={() => {
        dispatch({
          type: 'OPEN_SUMMARY_PANEL',
          nodeId: source,
          initialTab: TrustDetailsTabs.BENEFICIARIES,
        });
        dispatch({
          type: 'CLOSE_EDGE_LABEL',
          edgeId,
        });
      }}
    >
      View details
    </Button>
  );
}

export function createScheduledDistributionCard({
  source,
  target,
  sourceEntityName,
  targetEntityName,
  payments,
  dispatch,
}: ScheduledDistributionCardParams): CardConfig | null {
  const distribution = payments[0]?.associatedObject;
  if (distribution?.__typename !== 'ScheduledDistribution') return null;

  // drawEdges marks transfers that run opposite to the merged visual edge.
  // Payments grouped by schedule all have the same source and recipient.
  const reversed = (payments[0] as { reversed?: boolean }).reversed;
  const sourceName = reversed ? targetEntityName : sourceEntityName;
  const recipientName = reversed ? sourceEntityName : targetEntityName;

  // Transfer log amounts are annual disbursements, not recurring payment terms.
  // Show the configured schedule without projecting growth or available funds.
  let amount = 'Not specified';
  switch (distribution.distributionKind) {
    case ScheduledDistributionKind.Amount:
      if (distribution.amount != null)
        amount = formatCurrencyNoDecimals(distribution.amount);
      break;
    case ScheduledDistributionKind.Percentage:
      if (distribution.percentage != null)
        amount = `${formatPercentSmart(distribution.percentage)}% of the trust`;
      break;
    case ScheduledDistributionKind.AllIncome:
      amount =
        distribution.percentage != null
          ? `${formatPercentSmart(distribution.percentage)}% of trust income`
          : 'All income';
      break;
  }
  const frequency = SCHEDULED_DISTRIBUTION_FREQUENCY_ITEMS.find(
    (item) => item.value === distribution.frequency
  )?.display;
  const inflationSuffix = distribution.growByInflation
    ? ', inflation adjusted'
    : '';
  const rows: MiniTableRow[] = [
    { label: 'Distribution amount', value: `${amount}${inflationSuffix}` },
    {
      label: 'Frequency',
      value: frequency ?? 'Not specified',
    },
  ];
  const start = distribution.ageRequirementStart;
  const end = distribution.ageRequirementEnd;
  let ageRequirement: string | undefined;
  if (start != null && end != null) {
    ageRequirement = `Between ages ${start} and ${end}`;
  } else if (start != null) {
    ageRequirement = `Upon age ${start}`;
  } else if (end != null) {
    ageRequirement = `Until age ${end}`;
  }
  if (ageRequirement)
    rows.push({ label: 'Age requirement', value: ageRequirement });

  return {
    element: (
      <EdgeLabelCard
        key={`scheduled-distribution-${distribution.id}`}
        title="Scheduled distribution"
        description={`Scheduled distribution from ${sourceName} to ${recipientName}`}
        rows={rows}
      >
        <ViewBeneficiariesButton
          source={reversed ? target : source}
          edgeId={createEdgeId(source, target)}
          dispatch={dispatch}
        />
      </EdgeLabelCard>
    ),
    label: 'Scheduled distribution',
    value: amount,
    badgeValue: amount,
    variant: 'primary',
  };
}
